/**
 * @typedef {Object} AddressComponent
 * @property {string} long_name
 * @property {string} short_name
 * @property {string[]} types
 */
import { ADDRESS_COMPONENTS } from "@miplatform/constants";

export const MIN_STATE_ADDRESS_LENGTH = 2;

export const MIN_CITY_ADDRESS_LENGTH = 3;

/**
 * Get the city from a full address string.
 * @param {string} address - The full address.
 * @returns {string|null} - The city name or null if not found.
 */
export const getCityFromAddress = (address) => {
  const splittedAddress = address.split(",");

  if (splittedAddress.length < MIN_CITY_ADDRESS_LENGTH) {
    return null;
  }

  const splittedCity =
    splittedAddress[splittedAddress.length - MIN_CITY_ADDRESS_LENGTH].split(
      " "
    );
  splittedCity.shift();
  const city = splittedCity.join(" ");

  return city;
};

/**
 * Get the state from a full address string.
 * @param {string} address - The full address.
 * @returns {string|null} - The state name or null if not found.
 */
export const getStateFromAddress = (address) => {
  const splittedAddress = address.split(",");

  if (splittedAddress.length < MIN_STATE_ADDRESS_LENGTH) {
    return null;
  }

  const state = splittedAddress[
    splittedAddress.length - MIN_STATE_ADDRESS_LENGTH
  ]
    .replace(/[0-9]/g, "")
    .trim();

  return state;
};

/**
 * Get a specific address component by type.
 * @param {AddressComponent[]} components - The array of address components.
 * @param {string|string[]} types - The type(s) to match.
 * @returns {string|null} - The short name of the component or null if not found.
 */
export const getAddressComponent = (components, types) => {
  const component = components.find((comp) =>
    Array.isArray(types)
      ? types.some((type) => comp.types.includes(type))
      : comp.types.includes(types)
  );

  return component?.short_name || null;
};

/**
 * Get the state from address components.
 * @param {AddressComponent[]} components - The array of address components.
 * @returns {string|null} - The state name or null if not found.
 */
export const getStateFromAddressComponents = (components) =>
  getAddressComponent(components, ADDRESS_COMPONENTS.STATE);

/**
 * Get the country from address components.
 * @param {AddressComponent[]} components - The array of address components.
 * @returns {string|null} - The country name or null if not found.
 */
export const getCountryFromAddressComponents = (components) =>
  getAddressComponent(components, ADDRESS_COMPONENTS.COUNTRY);

/**
 * Get the county from address components.
 * @param {AddressComponent[]} components - The array of address components.
 * @returns {string|null} - The county name or null if not found.
 */
export const getCountyFromAddressComponents = (components) =>
  getAddressComponent(components, ADDRESS_COMPONENTS.COUNTY);

/**
 * Get the city from address components.
 * @param {AddressComponent[]} components - The array of address components.
 * @returns {string|null} - The city name or null if not found.
 */
export const getCityFromAddressComponents = (components) =>
  getAddressComponent(components, [
    ADDRESS_COMPONENTS.DISTRICT,
    ADDRESS_COMPONENTS.CITY,
  ]);

/**
 * Get the postal code from address components.
 * @param {AddressComponent[]} components - The array of address components.
 * @returns {string|null} - The postal code or null if not found.
 */
export const getPostalCodeFromAddressComponents = (components) =>
  getAddressComponent(components, ADDRESS_COMPONENTS.POSTAL_CODE);

/**
 * Get the city from prediction string.
 * @param {string} description - The prediction description.
 * @param {string|null} state - Optional state to filter by.
 * @returns {string|null} - The city name or null if not found.
 */
export const getCityFromPrediction = (description, state = null) => {
  const splittedPrediction = description.split(",");

  if (splittedPrediction.length < MIN_CITY_ADDRESS_LENGTH) {
    return null;
  }

  const predictionState =
    splittedPrediction[
      splittedPrediction.length - MIN_STATE_ADDRESS_LENGTH
    ].trim();

  if (state && predictionState !== state) {
    return null;
  }

  return splittedPrediction[0];
};

/**
 * Get the state from prediction string.
 * @param {string} description - The prediction description.
 * @returns {string|null} - The state name or null if not found.
 */
export const getStateFromPrediction = (description) => {
  const splittedPrediction = description.split(",");

  if (splittedPrediction.length < MIN_STATE_ADDRESS_LENGTH) {
    return null;
  }

  const predictionState =
    splittedPrediction[
      splittedPrediction.length - MIN_STATE_ADDRESS_LENGTH
    ].trim();

  return predictionState;
};

/**
 * Checks if a specified city is present in a given address.
 *
 * This function evaluates whether the address contains multiple components
 * (indicating it's a complete address) and compares the city derived from
 * the address components to the specified city name, ignoring case.
 *
 * @param {Object} address - The address object to be checked.
 * @param {string} address.formatted_address - The formatted address as a string.
 * @param {Array} address.address_components - The array of address components.
 * @param {string} [city] - The city name to compare against (optional).
 * @returns {boolean} - Returns true if the address has more than two components
 *                      and matches the specified city; otherwise, returns false.
 */
export const isCityInAddress = (address, city) => {
  const condition = address.formatted_address.split(",").length > 2;

  if (city) {
    return (
      condition &&
      getCityFromAddressComponents(
        address.address_components
      )?.toLowerCase() === city.toLowerCase()
    );
  }

  return condition;
};

export const calculateMapZoomLevel = (
  maxDiff,
  { minZoom = 1, maxZoom = 7 } = {}
) => {
  const zoom = Math.round(Math.log(360 / maxDiff) / Math.LN2);

  return Math.min(Math.max(zoom, minZoom), maxZoom);
};

export const getLocationBoundaries = (locations) => {
  const validLocations = locations.filter(
    (loc) => loc && (loc.Latitude !== 0 || loc.Longitude !== 0)
  );

  if (!validLocations.length) return {};

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  const MILES_PER_DEGREE = 69;

  validLocations.forEach((location) => {
    const radius = Number(location.Radius) || 0;

    const latOffset = radius / MILES_PER_DEGREE;

    const cosLat = Math.cos(location.Latitude * (Math.PI / 180));
    const lngOffset = radius / (MILES_PER_DEGREE * cosLat);

    const locMinLat = location.Latitude - latOffset;
    const locMaxLat = location.Latitude + latOffset;
    const locMinLng = location.Longitude - lngOffset;
    const locMaxLng = location.Longitude + lngOffset;

    minLat = Math.min(minLat, locMinLat);
    maxLat = Math.max(maxLat, locMaxLat);
    minLng = Math.min(minLng, locMinLng);
    maxLng = Math.max(maxLng, locMaxLng);
  });

  const latDiff = maxLat - minLat;
  const lngDiff = maxLng - minLng;
  const maxDiff = Math.max(latDiff, lngDiff);

  return {
    position: {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    },
    zoomFactor: calculateMapZoomLevel(maxDiff, { minZoom: 1, maxZoom: 10 }),
  };
};
